import { Vector3 } from 'three'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

/**
 * Area loads are rectangles in the plane of their element: `origin` is P0, `l1` the length of
 * the edge P0 → P1 (along `u`) and `l2` the length of the edge P1 → P2 (along `v`).
 */
export interface AreaLoadRectangle {
  origin: Vector3
  u: Vector3
  v: Vector3
  l1: number
  l2: number
}

export interface AreaLoadPoint {
  x: number
  y: number
  z: number
}

const MIN_LENGTH = 1e-9

const toVector = ({ x, y, z }: AreaLoadPoint) => new Vector3(x, y, z)

/** Plane normal of a polygon (Newell's method), pointing upwards. */
const getPolygonNormal = (points: Vector3[]): Vector3 => {
  const normal = new Vector3()

  points.forEach((current, index) => {
    const next = points[(index + 1) % points.length]
    normal.x += (current.y - next.y) * (current.z + next.z)
    normal.y += (current.z - next.z) * (current.x + next.x)
    normal.z += (current.x - next.x) * (current.y + next.y)
  })

  normal.normalize()

  return normal.z < 0 ? normal.negate() : normal
}

/**
 * Reads the rectangle of an area load. Only P1, P2 and P3 are used (P0 = P1 - l1 * u), so the
 * rectangle stays intact while P0 is being edited. Returns undefined for shapes that are not
 * quadrilaterals.
 */
export const toAreaLoadRectangle = (points: AreaLoadPoint[]): AreaLoadRectangle | undefined => {
  if (points.length !== 4 || points.some(point => !point)) return undefined

  const [, p1, p2, p3] = points.map(toVector)

  const edge1 = p2.clone().sub(p3)
  const edge2 = p2.clone().sub(p1)
  const l1 = edge1.length()
  const l2 = edge2.length()

  if (l1 < MIN_LENGTH || l2 < MIN_LENGTH) return undefined

  const u = edge1.divideScalar(l1)
  // drop any component along u so that the rebuilt shape is a proper rectangle
  const v = edge2.sub(u.clone().multiplyScalar(edge2.dot(u))).normalize()

  return { origin: p1.clone().addScaledVector(u, -l1), u, v, l1, l2 }
}

export const toAreaLoadRectanglePoints = ({
  origin,
  u,
  v,
  l1,
  l2,
}: AreaLoadRectangle): AreaLoadPoint[] =>
  [
    origin.clone(),
    origin.clone().addScaledVector(u, l1),
    origin.clone().addScaledVector(u, l1).addScaledVector(v, l2),
    origin.clone().addScaledVector(v, l2),
  ].map(({ x, y, z }) => ({ x, y, z }))

export const toAreaOfAttack = (rectangle: AreaLoadRectangle): Polygon => ({
  points: toAreaLoadRectanglePoints(rectangle).map(({ x, y, z }) => new ImmutableVector3(x, y, z)),
})

/**
 * Smallest rectangle in the plane of `points` that covers them, aligned with the edge P0 → P1.
 * For a rectangular element this is the element itself.
 */
export const getBoundingAreaLoadRectangle = (
  points: AreaLoadPoint[],
): AreaLoadRectangle | undefined => {
  if (points.length < 3) return undefined

  const vectors = points.map(toVector)
  const [p0, p1] = vectors

  const u = p1.clone().sub(p0)

  if (u.length() < MIN_LENGTH) return undefined

  u.normalize()

  const v = getPolygonNormal(vectors).cross(u).normalize()

  const alongU = vectors.map(point => point.clone().sub(p0).dot(u))
  const alongV = vectors.map(point => point.clone().sub(p0).dot(v))

  const minU = Math.min(...alongU)
  const minV = Math.min(...alongV)

  return {
    origin: p0.clone().addScaledVector(u, minU).addScaledVector(v, minV),
    u,
    v,
    l1: Math.max(...alongU) - minU,
    l2: Math.max(...alongV) - minV,
  }
}

/** Moves the rectangle so that P0 is at (x, y); z follows the plane of the rectangle. */
export const moveAreaLoadRectangleOrigin = (
  rectangle: AreaLoadRectangle,
  x: number,
  y: number,
): AreaLoadRectangle => {
  const { origin, u, v } = rectangle
  const normal = u.clone().cross(v)
  const z = origin.z - (normal.x * (x - origin.x) + normal.y * (y - origin.y)) / normal.z

  return { ...rectangle, origin: new Vector3(x, y, z) }
}
